import noop from 'lodash/noop';
import { absorber } from './absorber';
import { getDocumentMirror } from './documentMirror';
import { getLocationMirror } from './locationMirror';
import { getNavigatorMirror } from './navigatorMirror';
import { getSafeWindowMirror } from './safeWindowMirror';

// Every name here becomes a parameter of the compiled expression function, bound to
// the absorber. Each must be a legal strict-mode binding identifier or getSafeBindings
// silently drops it and the "shadow" never exists — which is why `eval` is NOT here
// (strict-mode reserved, un-shadowable by this mechanism; documented residual next to
// dynamic import). The modes test suite probes each entry through a compiled evaluator.
export const READ_ONLY_ABSORBED_GLOBALS = [
  'localStorage',
  'sessionStorage',
  'history',
  'navigation',
  'fetch',
  'XMLHttpRequest',
  'WebSocket',
  'EventSource',
  'RTCPeerConnection',
  'WebTransport',
  'Worker',
  'SharedWorker',
  'indexedDB',
  'caches',
  'cookieStore',
  'showOpenFilePicker',
  'showSaveFilePicker',
  'showDirectoryPicker',
  'open',
  'close',
  'print',
  'postMessage',
  'BroadcastChannel',
  'alert',
  'prompt',
  'confirm',
  'setTimeout',
  'setInterval',
  'clearTimeout',
  'clearInterval',
  'queueMicrotask',
  'requestAnimationFrame',
  'requestIdleCallback',
  'scheduler',
  'addEventListener',
  'removeEventListener',
  'dispatchEvent',
  'Notification',
  'Function',
  'importScripts',
] as const;

let cachedContext: Record<string, unknown> | null = null;

// Built lazily on first evaluation, never at import: mirror construction is cheap and
// window-free, but keeping it lazy means SSR/native imports can never pay or break.
export function getReadOnlySafeGlobalContext(): Record<string, unknown> {
  if (cachedContext) return cachedContext;

  const windowMirror = getSafeWindowMirror();
  const context: Record<string, unknown> = {
    // Same escape guard the permissive context carries today.
    constructor: { constructor: noop },
    window: windowMirror,
    self: windowMirror,
    globalThis: windowMirror,
    top: windowMirror,
    parent: windowMirror,
    frames: windowMirror,
    opener: windowMirror,
    frameElement: windowMirror,
    location: getLocationMirror(),
    navigator: getNavigatorMirror(),
    document: getDocumentMirror(),
  };
  for (const globalName of READ_ONLY_ABSORBED_GLOBALS) {
    context[globalName] = absorber;
  }

  cachedContext = context;
  return context;
}
